/**
 * Mode Controller - Sentinel-E
 *
 * Resolves rendering mode from backend response metadata.
 * Supports: standard, debate, glass, evidence, ensemble, forensic
 */

/**
 * Resolve rendering mode from backend response.
 * Reads omega_metadata.sub_mode / mode to determine which view to render.
 */
export function resolveRenderMode(result) {
  if (!result || !result.omega_metadata) {
    return { mode: 'standard', engine: 'StandardKernel', data: null };
  }

  const meta = result.omega_metadata;
  const mode = meta.sub_mode || meta.mode || result.sub_mode || result.mode || 'standard';

  const base = {
    mode,
    data: meta,
    boundary: result.boundary_result || meta.boundary_result || {},
    confidence: result.confidence ?? meta.confidence ?? 0.5,
  };

  // Debate mode — MUST check before ensemble so sub_mode="debate" isn't overridden
  if (mode === 'debate') {
    // Debate responses from CognitiveOrchestrator also carry ensemble_metrics.
    // Merge ensemble data into the debate payload so DebateView has full access.
    // DebateView reads data.rounds, data.analysis, data.scores, data.models_used
    // so data MUST be the debate_result object, not the raw omega_metadata.
    const debateResult = meta.debate_result || meta.aggregation_result || {};
    return {
      ...base,
      mode: 'debate',
      engine: 'DebateEngine',
      data: debateResult,
      debateResult: debateResult,
      rounds: debateResult.rounds || [],
      analysis: debateResult.analysis || {},
      modelOutputs: meta.model_outputs || meta.aggregation_result?.model_outputs || [],
      // Ensemble-level data (available when CognitiveOrchestrator ran)
      ensembleMetrics: meta.ensemble_metrics || {},
      debateRounds: meta.debate_rounds || [],
      agreementMatrix: meta.agreement_matrix || {},
      driftMetrics: meta.drift_metrics || {},
      modelStatus: meta.model_status || [],
      sessionIntelligence: meta.session_intelligence || {},
      calibratedConfidence: meta.confidence_graph || meta.calibrated_confidence || {},
      tacticalMap: meta.tactical_map || {},
    };
  }

  // Ensemble mode — full cognitive pipeline data
  // Only enter ensemble rendering if EXPLICITLY in ensemble/cognitive mode,
  // not when a sub-mode (glass/evidence/synthesis) is active with ensemble metrics.
  if ((mode === 'ensemble' || mode === 'cognitive') ||
      (meta.ensemble_metrics && !['glass', 'evidence', 'synthesis'].includes(mode))) {
    return {
      ...base,
      mode: 'ensemble',
      engine: 'CognitiveCoreEngine',
      entropy: result.entropy ?? meta.entropy ?? 0,
      fragility: result.fragility ?? meta.fragility ?? 0,
      ensembleMetrics: meta.ensemble_metrics || {},
      debateRounds: meta.debate_rounds || [],
      agreementMatrix: meta.agreement_matrix || {},
      driftMetrics: meta.drift_metrics || {},
      modelStatus: meta.model_status || [],
      sessionIntelligence: meta.session_intelligence || {},
      tacticalMap: meta.tactical_map || {},
      calibratedConfidence: meta.confidence_graph || meta.calibrated_confidence || {},
      modelOutputs: meta.model_outputs || [],
      modelsExecuted: result.models_executed ?? meta.ensemble_metrics?.model_count ?? 0,
      modelsSucceeded: result.models_succeeded ?? meta.ensemble_metrics?.successful_models ?? 0,
      modelsFailed: result.models_failed ?? meta.ensemble_metrics?.failed_models ?? 0,
      error: result.error || meta.error || null,
      errorCode: result.error_code || meta.error_code || null,
    };
  }

  // Glass mode
  if (mode === 'glass') {
    const auditResult = meta.audit_result || {};
    return {
      ...base,
      data: auditResult,
      engine: 'GlassEngine',
      auditResult,
      reasoningTrace: result.reasoning_trace || meta.reasoning_trace || {},
    };
  }

  // Evidence mode
  if (mode === 'evidence') {
    const forensicResult = meta.forensic_result || {};
    return {
      ...base,
      data: forensicResult,
      engine: 'EvidenceEngine',
      forensicResult,
      sources: forensicResult.sources || [],
    };
  }

  // Synthesis mode
  if (mode === 'synthesis') {
    const synthesisResult = meta.synthesis_result || {};
    return {
      ...base,
      data: synthesisResult,
      engine: 'SynthesisEngine',
      synthesisResult,
    };
  }

  // Standard / conversational (default)
  return {
    ...base,
    engine: 'StandardKernel',
    aggregationResult: meta.aggregation_result || {},
  };
}

/**
 * Extract pipeline steps from result for ThinkingAnimation
 */
export function extractPipelineSteps(result) {
  if (!result) return [];
  const meta = result.omega_metadata;
  if (!meta) return [];
  if (meta.pipeline_steps) {
    return meta.pipeline_steps.map((step, i) => ({
      id: i,
      step: step.step || step.name || `step_${i}`,
      label: step.label || step.name || 'Processing...',
      status: step.status || 'pending',
    }));
  }
  return [];
}

/**
 * Default pipeline steps based on mode.
 */
export function getDefaultPipelineSteps(mode, subMode) {
  const effectiveMode = subMode || mode || 'standard';

  if (effectiveMode === 'ensemble') {
    return [
      { id: 0, step: 'validate', label: 'Validating Providers', status: 'pending' },
      { id: 1, step: 'execute', label: 'Executing All Models', status: 'pending' },
      { id: 2, step: 'structure', label: 'Parsing Structured Outputs', status: 'pending' },
      { id: 3, step: 'debate', label: 'Running Structured Debate', status: 'pending' },
      { id: 4, step: 'agreement', label: 'Computing Agreement Matrix', status: 'pending' },
      { id: 5, step: 'drift', label: 'Tracking Stance Drift', status: 'pending' },
      { id: 6, step: 'metrics', label: 'Computing Ensemble Metrics', status: 'pending' },
      { id: 7, step: 'calibrate', label: 'Calibrating Confidence', status: 'pending' },
      { id: 8, step: 'tactical', label: 'Building Tactical Map', status: 'pending' },
      { id: 9, step: 'synthesis', label: 'Synthesizing Consensus', status: 'pending' },
      { id: 10, step: 'session', label: 'Updating Session Intelligence', status: 'pending' },
      { id: 11, step: 'rendering', label: 'Rendering', status: 'pending' },
    ];
  }

  if (effectiveMode === 'debate') {
    return [
      { id: 0, step: 'context', label: 'Stabilizing Context', status: 'pending' },
      { id: 1, step: 'routing', label: 'Routing to Debate Engine', status: 'pending' },
      { id: 2, step: 'round1', label: 'Debate Round 1', status: 'pending' },
      { id: 3, step: 'round2', label: 'Debate Round 2', status: 'pending' },
      { id: 4, step: 'round3', label: 'Debate Round 3', status: 'pending' },
      { id: 5, step: 'synthesis', label: 'Synthesizing Arguments', status: 'pending' },
      { id: 6, step: 'rendering', label: 'Rendering', status: 'pending' },
    ];
  }

  if (effectiveMode === 'glass') {
    return [
      { id: 0, step: 'context', label: 'Stabilizing Context', status: 'pending' },
      { id: 1, step: 'execute', label: 'Executing All Models', status: 'pending' },
      { id: 2, step: 'audit', label: 'Running Blind Forensic Audit', status: 'pending' },
      { id: 3, step: 'trace', label: 'Extracting Reasoning Trace', status: 'pending' },
      { id: 4, step: 'graph', label: 'Building Reasoning Graph', status: 'pending' },
      { id: 5, step: 'trust', label: 'Computing Trust Scores', status: 'pending' },
      { id: 6, step: 'rendering', label: 'Rendering', status: 'pending' },
    ];
  }

  if (effectiveMode === 'evidence') {
    return [
      { id: 0, step: 'context', label: 'Stabilizing Context', status: 'pending' },
      { id: 1, step: 'search', label: 'Searching Evidence Sources', status: 'pending' },
      { id: 2, step: 'execute', label: 'Executing Model Analysis', status: 'pending' },
      { id: 3, step: 'claims', label: 'Extracting Claims', status: 'pending' },
      { id: 4, step: 'verify', label: 'Cross-Referencing Sources', status: 'pending' },
      { id: 5, step: 'contradictions', label: 'Detecting Contradictions', status: 'pending' },
      { id: 6, step: 'confidence', label: 'Computing Bayesian Confidence', status: 'pending' },
      { id: 7, step: 'rendering', label: 'Rendering', status: 'pending' },
    ];
  }

  if (effectiveMode === 'synthesis') {
    return [
      { id: 0, step: 'context', label: 'Stabilizing Context', status: 'pending' },
      { id: 1, step: 'draft', label: 'Generating Primary Draft', status: 'pending' },
      { id: 2, step: 'review', label: 'Peer Model Review', status: 'pending' },
      { id: 3, step: 'integrate', label: 'Integrating Feedback', status: 'pending' },
      { id: 4, step: 'consensus', label: 'Computing Consensus', status: 'pending' },
      { id: 5, step: 'rendering', label: 'Rendering', status: 'pending' },
    ];
  }

  // Standard / conversational
  return [
    { id: 0, step: 'context', label: 'Stabilizing Context', status: 'pending' },
    { id: 1, step: 'routing', label: 'Routing Query', status: 'pending' },
    { id: 2, step: 'inference', label: 'Running Inference', status: 'pending' },
    { id: 3, step: 'boundary', label: 'Boundary Analysis', status: 'pending' },
    { id: 4, step: 'rendering', label: 'Rendering', status: 'pending' },
  ];
}

const modeController = { resolveRenderMode, extractPipelineSteps, getDefaultPipelineSteps };
export default modeController;
